import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext.jsx';
import Header from '../components/Header.jsx';
import { formatDateTime, getUserTimezone } from '../utils/dateUtils.js';
import LoadingSpinner from '../components/LoadingSpinner.jsx';
import ErrorMessage from '../components/ErrorMessage.jsx';
import { colors, typography, spacing, radius, components } from '../../styles/theme.js';

// Common timezones for the dropdown
const COMMON_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Toronto',
  'America/Vancouver',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Moscow',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Hong_Kong',
  'Asia/Singapore',
  'Asia/Dubai',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Pacific/Auckland'
];

const ProfilePage = () => {
  const { user, updateProfile } = useAuth();
  const [editing, setEditing] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState(user?.timezone || getUserTimezone());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSaveTimezone = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    const result = await updateProfile({ timezone: selectedTimezone });
    
    if (result.success) {
      setSuccess('Timezone updated successfully!');
      setEditing(false);
      setTimeout(() => setSuccess(''), 3000);
    } else {
      setError(result.error || 'Failed to update timezone');
    }
    
    setSaving(false);
  };

  const handleCancel = () => {
    setSelectedTimezone(user?.timezone || getUserTimezone());
    setEditing(false);
    setError('');
  };

  const metadata = user?.metadata || {};

  return (
    <>
      <Header />
      <div style={{
        minHeight: 'calc(100vh - 64px)',
        background: colors.bgPrimary,
        padding: spacing[8],
        fontFamily: typography.fontSans
      }}>
        <div style={{
          maxWidth: '900px',
          margin: '0 auto'
        }}>
          {/* Header */}
          <div style={{ marginBottom: spacing[8] }}>
            <h1 style={{
              fontSize: typography['4xl'],
              fontWeight: typography.extrabold,
              marginBottom: spacing[2],
              color: colors.textPrimary,
              letterSpacing: '-0.02em',
              lineHeight: typography.tight
            }}>
              Profile
            </h1>
            
            <p style={{
              color: colors.textTertiary,
              fontSize: typography.base,
              margin: 0
            }}>
              Manage your account settings
            </p>
          </div>

          {/* Success Message */}
          {success && (
            <div style={{
              background: colors.success,
              border: `1px solid ${colors.successBorder}`,
              borderRadius: radius.lg,
              padding: spacing[4],
              marginBottom: spacing[6],
              color: colors.successText,
              fontSize: typography.sm,
              fontWeight: typography.medium
            }}>
              ✓ {success}
            </div>
          )}

          {/* Error Message */}
          {error && <div style={{ marginBottom: spacing[6] }}><ErrorMessage error={error} /></div>}

          {/* Account Information */}
          <div style={{
            ...components.card,
            padding: spacing[6],
            marginBottom: spacing[6]
          }}>
            <h2 style={{
              fontSize: typography.xl,
              fontWeight: typography.bold,
              color: colors.textPrimary,
              marginBottom: spacing[5],
              letterSpacing: '-0.01em'
            }}>
              Account Information
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[4] }}>
              <div>
                <div style={{ 
                  fontSize: typography.xs, 
                  color: colors.textTertiary, 
                  marginBottom: spacing[1],
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontWeight: typography.medium
                }}>
                  Email
                </div>
                <div style={{ color: colors.textPrimary, fontWeight: typography.medium }}>
                  {user?.email}
                </div>
              </div>

              <div>
                <div style={{ 
                  fontSize: typography.xs, 
                  color: colors.textTertiary, 
                  marginBottom: spacing[1],
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  fontWeight: typography.medium
                }}>
                  Member Since
                </div>
                <div style={{ color: colors.textPrimary, fontWeight: typography.medium }}>
                  {formatDateTime(user?.createdAt)}
                </div>
              </div>
            </div>
          </div>

          {/* Timezone Settings */}
          <div style={{
            ...components.card,
            padding: spacing[6],
            marginBottom: spacing[6]
          }}>
            <h2 style={{
              fontSize: typography.xl,
              fontWeight: typography.bold,
              color: colors.textPrimary,
              marginBottom: spacing[3],
              letterSpacing: '-0.01em'
            }}>
              Timezone
            </h2>

            <p style={{
              fontSize: typography.sm,
              color: colors.textTertiary,
              marginBottom: spacing[5],
              lineHeight: typography.normal
            }}>
              Used to group training rounds into daily sessions
            </p>

            {!editing ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ 
                    fontSize: typography.xs, 
                    color: colors.textTertiary, 
                    marginBottom: spacing[1],
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    fontWeight: typography.medium
                  }}>
                    Current
                  </div>
                  <div style={{ color: colors.textPrimary, fontWeight: typography.medium }}>
                    {user?.timezone || 'UTC'}
                  </div>
                </div>
                <button
                  onClick={() => setEditing(true)}
                  style={{
                    padding: `${spacing[2]} ${spacing[4]}`,
                    borderRadius: radius.md,
                    border: `1px solid ${colors.borderLight}`,
                    background: 'transparent',
                    color: colors.textSecondary,
                    fontSize: typography.sm,
                    fontWeight: typography.medium,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    fontFamily: typography.fontSans
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = colors.primary;
                    e.currentTarget.style.color = colors.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = colors.borderLight;
                    e.currentTarget.style.color = colors.textSecondary;
                  }}
                >
                  Change
                </button>
              </div>
            ) : (
              <div>
                <div style={{ position: 'relative' }}>
                  <select
                    value={selectedTimezone}
                    onChange={(e) => setSelectedTimezone(e.target.value)}
                    disabled={saving}
                    style={{
                      width: '100%',
                      padding: spacing[4],
                      borderRadius: radius.lg,
                      border: 'none',
                      background: 'rgba(30, 41, 59, 0.6)',
                      color: colors.textPrimary,
                      fontSize: typography.base,
                      marginBottom: spacing[4],
                      cursor: saving ? 'not-allowed' : 'pointer',
                      fontFamily: typography.fontSans,
                      fontWeight: typography.semibold,
                      transition: 'all 0.15s ease',
                      appearance: 'none',
                      paddingRight: '3rem',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      opacity: saving ? 0.6 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!saving) {
                        e.currentTarget.style.background = 'rgba(30, 41, 59, 0.8)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!saving) {
                        e.currentTarget.style.background = 'rgba(30, 41, 59, 0.6)';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                  >
                    {COMMON_TIMEZONES.map(tz => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                  <div style={{
                    position: 'absolute',
                    right: spacing[4],
                    top: '50%',
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    color: colors.textTertiary
                  }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: spacing[3] }}>
                  <button
                    onClick={handleSaveTimezone}
                    disabled={saving}
                    style={{
                      flex: 1,
                      padding: spacing[3],
                      borderRadius: radius.lg,
                      border: 'none',
                      background: saving ? colors.bgInput : `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                      color: '#fff',
                      fontSize: typography.sm,
                      fontWeight: typography.semibold,
                      cursor: saving ? 'not-allowed' : 'pointer',
                      opacity: saving ? 0.6 : 1,
                      fontFamily: typography.fontSans,
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={saving}
                    style={{
                      flex: 1,
                      padding: spacing[3],
                      borderRadius: radius.lg,
                      border: `1px solid ${colors.borderLight}`,
                      background: 'transparent',
                      color: colors.textSecondary,
                      fontSize: typography.sm,
                      fontWeight: typography.semibold,
                      cursor: saving ? 'not-allowed' : 'pointer',
                      fontFamily: typography.fontSans,
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!saving) {
                        e.currentTarget.style.borderColor = colors.border;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!saving) {
                        e.currentTarget.style.borderColor = colors.borderLight;
                      }
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Training Stats Summary */}
          <div style={{
            ...components.card,
            padding: spacing[6]
          }}>
            <h2 style={{
              fontSize: typography.xl,
              fontWeight: typography.bold,
              color: colors.textPrimary,
              marginBottom: spacing[6],
              letterSpacing: '-0.01em'
            }}>
              Training Statistics
            </h2>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', 
              gap: spacing[5]
            }}>
              <div>
                <div style={{ 
                  fontSize: typography['3xl'], 
                  fontWeight: typography.bold, 
                  color: colors.primary,
                  marginBottom: spacing[1],
                  lineHeight: typography.tight,
                  letterSpacing: '-0.02em'
                }}>
                  {metadata.totalRounds || 0}
                </div>
                <div style={{ 
                  fontSize: typography.sm, 
                  color: colors.textTertiary,
                  fontWeight: typography.medium
                }}>
                  Total Rounds
                </div>
              </div>

              <div>
                <div style={{ 
                  fontSize: typography['3xl'], 
                  fontWeight: typography.bold, 
                  color: colors.secondary,
                  marginBottom: spacing[1],
                  lineHeight: typography.tight,
                  letterSpacing: '-0.02em'
                }}>
                  {metadata.totalSessions || 0}
                </div>
                <div style={{ 
                  fontSize: typography.sm, 
                  color: colors.textTertiary,
                  fontWeight: typography.medium
                }}>
                  Total Sessions
                </div>
              </div>

              <div>
                <div style={{ 
                  fontSize: typography['3xl'], 
                  fontWeight: typography.bold, 
                  color: colors.accent,
                  marginBottom: spacing[1],
                  lineHeight: typography.tight,
                  letterSpacing: '-0.02em'
                }}>
                  {metadata.totalDaysTrained || 0}
                </div>
                <div style={{ 
                  fontSize: typography.sm, 
                  color: colors.textTertiary,
                  fontWeight: typography.medium
                }}>
                  Days Trained
                </div>
              </div>

              <div>
                <div style={{ 
                  fontSize: typography['3xl'], 
                  fontWeight: typography.bold, 
                  color: colors.warning,
                  marginBottom: spacing[1],
                  lineHeight: typography.tight,
                  letterSpacing: '-0.02em'
                }}>
                  {metadata.dimensionCombos?.length || 0}
                </div>
                <div style={{ 
                  fontSize: typography.sm, 
                  color: colors.textTertiary,
                  fontWeight: typography.medium
                }}>
                  Combos Tried
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfilePage;

